// dnPopup — lightweight themed div popups (drop-in replacement for SweetAlert).
// API mirrors Swal so calls can be swapped 1:1:
//   dnPopup.fire("Title", "text", "success")
//   dnPopup.fire({ icon, title, text, showCancelButton, confirmButtonText, cancelButtonText })
//   -> resolves to { isConfirmed, isDismissed }
//   dnPopup.success/error/warning/info/confirm/toast(...)
//
// Styles live in theme.css (.dn-pop-* / .dn-toast-*).

const ICONS = {
    success: "✓",
    error: "✕",
    warning: "!",
    info: "i",
    question: "?",
};

function closeWith(overlay, onResolve, result, keyHandler) {
    if (overlay.__closing) return;
    overlay.__closing = true;
    document.removeEventListener("keydown", keyHandler);
    overlay.classList.add("dn-pop-closing");
    setTimeout(() => {
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        onResolve(result);
    }, 130);
}

function fire(arg1, arg2, arg3) {
    const opts =
        arg1 && typeof arg1 === "object"
            ? arg1
            : { title: arg1, text: arg2, icon: arg3 };

    const {
        title = "",
        text = "",
        icon,
        showCancelButton = false,
        confirmButtonText = "OK",
        cancelButtonText = "Cancel",
        danger = false,
    } = opts;

    return new Promise((resolve) => {
        const overlay = document.createElement("div");
        overlay.className = "dn-pop-overlay";

        const card = document.createElement("div");
        card.className = "dn-pop-card";
        card.setAttribute("role", "dialog");
        card.setAttribute("aria-modal", "true");

        const iconHtml = icon
            ? `<div class="dn-pop-icon dn-pop-icon-${icon}">${ICONS[icon] || ""}</div>`
            : "";

        card.innerHTML = `
            ${iconHtml}
            ${title ? `<div class="dn-pop-title"></div>` : ""}
            ${text ? `<div class="dn-pop-text"></div>` : ""}
            <div class="dn-pop-actions">
                ${showCancelButton ? `<button type="button" class="dn-pop-btn dn-pop-cancel"></button>` : ""}
                <button type="button" class="dn-pop-btn dn-pop-confirm${danger ? " dn-pop-danger" : ""}"></button>
            </div>
        `;

        // Set text via textContent to avoid HTML injection
        if (title) card.querySelector(".dn-pop-title").textContent = title;
        if (text) card.querySelector(".dn-pop-text").textContent = text;
        const confirmBtn = card.querySelector(".dn-pop-confirm");
        confirmBtn.textContent = confirmButtonText;
        const cancelBtn = card.querySelector(".dn-pop-cancel");
        if (cancelBtn) cancelBtn.textContent = cancelButtonText;

        overlay.appendChild(card);
        document.body.appendChild(overlay);

        const keyHandler = (e) => {
            if (e.key === "Escape") {
                closeWith(overlay, resolve, { isConfirmed: false, isDismissed: true }, keyHandler);
            } else if (e.key === "Enter") {
                closeWith(overlay, resolve, { isConfirmed: true, isDismissed: false }, keyHandler);
            }
        };
        document.addEventListener("keydown", keyHandler);

        confirmBtn.addEventListener("click", () =>
            closeWith(overlay, resolve, { isConfirmed: true, isDismissed: false }, keyHandler)
        );
        if (cancelBtn) {
            cancelBtn.addEventListener("click", () =>
                closeWith(overlay, resolve, { isConfirmed: false, isDismissed: true }, keyHandler)
            );
        }
        overlay.addEventListener("mousedown", (e) => {
            if (e.target === overlay) {
                closeWith(overlay, resolve, { isConfirmed: false, isDismissed: true }, keyHandler);
            }
        });

        // focus the primary action
        setTimeout(() => confirmBtn.focus(), 0);
    });
}

function toast(title = "", icon = "success") {
    const el = document.createElement("div");
    el.className = `dn-toast dn-toast-${icon}`;
    el.innerHTML = `<span class="dn-toast-icon">${ICONS[icon] || ""}</span><span class="dn-toast-msg"></span>`;
    el.querySelector(".dn-toast-msg").textContent = title;
    document.body.appendChild(el);
    setTimeout(() => el.classList.add("dn-toast-out"), 2400);
    setTimeout(() => {
        if (el.parentNode) el.parentNode.removeChild(el);
    }, 2700);
}

const dnPopup = {
    fire,
    success: (title = "Success", text = "") => fire({ icon: "success", title, text }),
    error: (title = "Error", text = "") => fire({ icon: "error", title, text }),
    warning: (title = "Warning", text = "") => fire({ icon: "warning", title, text }),
    info: (title = "Info", text = "") => fire({ icon: "info", title, text }),
    // Supports both confirm("Title", "text") and
    // confirm({ title, message|text, confirmLabel, cancelLabel, danger }). Resolves to a boolean.
    confirm: (arg1 = "Are you sure?", text = "", confirmButtonText = "Yes") => {
        const o =
            arg1 && typeof arg1 === "object"
                ? {
                    title: arg1.title,
                    text: arg1.message != null ? arg1.message : arg1.text || "",
                    confirmButtonText: arg1.confirmLabel || "Yes",
                    cancelButtonText: arg1.cancelLabel || "Cancel",
                    danger: !!arg1.danger,
                }
                : { title: arg1, text, confirmButtonText };
        return fire({
            icon: o.danger ? "warning" : "question",
            showCancelButton: true,
            ...o,
        }).then((r) => r.isConfirmed);
    },
    toast,
};

export default dnPopup;
